import React, { createContext, useContext, useReducer, useCallback, useState, useEffect } from 'react';
import { notificationService } from '../services/notificationService';

const NotificationContext = createContext();

export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info',
};

let notificationCounter = 0;

const MAX_NOTIFICATIONS = 5; // Maximum number of notifications to show at once

const notificationReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      // Keep only the last MAX_NOTIFICATIONS notifications
      const newState = [...state, action.payload];
      if (newState.length > MAX_NOTIFICATIONS) {
        return newState.slice(-MAX_NOTIFICATIONS);
      }
      return newState;
    case 'REMOVE_NOTIFICATION':
      return state.filter((notification) => notification.id !== action.payload);
    default:
      return state;
  }
};

export const NotificationProvider = ({ children }) => {
  const [notifications, dispatch] = useReducer(notificationReducer, []);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState([]);

  // Listen for new notifications from the notification service
  useEffect(() => {
    const cleanup = notificationService.onNotification((message) => {
      // Show a toast notification for the new message
      showNotification(message.content, NOTIFICATION_TYPES.INFO);
      
      // Add to unread messages
      setUnreadMessages(prev => [...prev, message]);
      setUnreadCount(prev => prev + 1);
    });
    
    return cleanup;
  }, []);

  const showNotification = useCallback((message, type = NOTIFICATION_TYPES.SUCCESS, duration = 5000) => {
    if (!message) return; // Don't show empty notifications
    
    // Generate unique ID using timestamp + counter to avoid duplicates
    const id = `${Date.now()}_${++notificationCounter}`;
    
    console.log('Showing notification:', { message, type, duration, id });
    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: { message, type, id },
    });

    // Auto-dismiss after specified duration (default 5 seconds)
    setTimeout(() => {
      dispatch({
        type: 'REMOVE_NOTIFICATION',
        payload: id,
      });
    }, duration);
  }, []);

  const removeNotification = useCallback((id) => {
    dispatch({
      type: 'REMOVE_NOTIFICATION',
      payload: id,
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setUnreadCount(0);
    setUnreadMessages([]);
  }, []);

  const markAsRead = useCallback((messageId) => {
    setUnreadMessages(prev => prev.filter(msg => msg.id !== messageId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        removeNotification,
        unreadCount,
        unreadMessages,
        markAllAsRead,
        markAsRead
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}; 